import { styled } from '@mui/material';
import type { ReactNode } from 'react';

import { Tag } from '../../atoms/Tag';
import { MEMBER_AVATAR_SIZE, MEMBER_LIST_WIDE_QUERY } from '../MemberList/memberListContainer';

type Props = {
  name: string;
  /** Initials shown in the avatar, e.g. "CD". */
  initials: string;
  /** Role in the space, e.g. "Conseil syndical". */
  role: string;
  email?: string | null;
  /** Marks the signed-in user's own row "(vous)". */
  isCurrentUser?: boolean;
  /** Secondary controls (resource menu). */
  action?: ReactNode;
  'data-testid'?: string;
};

const Row = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${MEMBER_AVATAR_SIZE}px minmax(0, 1fr) auto`,
  gridTemplateAreas: `
    "avatar name trailing"
    "avatar role trailing"
    "avatar email trailing"`,
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  alignItems: 'center',
  minHeight: theme.spacing(9),
  paddingBlock: theme.spacing(1.5),
  paddingInline: theme.spacing(2),
  [MEMBER_LIST_WIDE_QUERY]: {
    gridTemplateColumns: `${MEMBER_AVATAR_SIZE}px minmax(0, 1fr) minmax(10rem, 0.4fr) auto`,
    gridTemplateAreas: `
      "avatar name role trailing"
      "avatar email role trailing"`,
    columnGap: theme.spacing(2),
    rowGap: 0,
  },
}));

const Initials = styled('span')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  gridArea: 'avatar',
  alignSelf: 'start',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: MEMBER_AVATAR_SIZE,
  height: MEMBER_AVATAR_SIZE,
  borderRadius: '50%',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.elevation1,
  color: theme.palette.text.secondary,
  [MEMBER_LIST_WIDE_QUERY]: {
    alignSelf: 'center',
  },
}));

const NameLine = styled('span')(({ theme }) => ({
  gridArea: 'name',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  minWidth: 0,
}));

const Name = styled('span')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  minWidth: 0,
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
}));

const YouLabel = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

const RoleSlot = styled('span')({
  gridArea: 'role',
  display: 'flex',
  minWidth: 0,
});

const EmailLink = styled('a')(({ theme }) => ({
  ...theme.typography.body2,
  gridArea: 'email',
  justifySelf: 'start',
  maxWidth: '100%',
  minWidth: 0,
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  borderRadius: theme.shape.borderRadius,
  '@media (hover: hover)': {
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      textUnderlineOffset: '0.2em',
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const Trailing = styled('span')(({ theme }) => ({
  gridArea: 'trailing',
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(-1),
}));

/**
 * One member of a space, inside a `MemberList`: initials, name, role and e-mail
 * (a `mailto:` link so a member is reached in one tap). The optional action slot hosts the
 * resource menu; the signed-in user's own row is marked "(vous)".
 */
export function MemberListItem(props: Props) {
  return (
    <li data-testid={props['data-testid'] ?? 'member-list-item'}>
      <Row>
        <Initials aria-hidden="true">{props.initials}</Initials>
        <NameLine>
          <Name>{props.name}</Name>
          {props.isCurrentUser ? <YouLabel>(vous)</YouLabel> : null}
        </NameLine>
        <RoleSlot>
          <Tag color="default" label={props.role} />
        </RoleSlot>
        {props.email ? (
          <EmailLink href={`mailto:${props.email}`}>{props.email}</EmailLink>
        ) : null}
        {props.action ? <Trailing>{props.action}</Trailing> : null}
      </Row>
    </li>
  );
}

export type { Props as MemberListItemProps };
